import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconBarbell,
  IconTargetArrow,
  IconTool,
  IconChartBar,
  IconTrendingUp,
  IconTarget,
  IconRocket,
  IconClock,
} from '@tabler/icons-react';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeLink } from '@/components/ui/tee-link';
import {
  PlaybookFragment,
  PlaybookSection,
  PhaseData,
  FunnelStepData,
  PersonaData,
  IdeaData,
  PostIdeaData,
  ScheduleData,
  HowToData,
  OutreachStepData,
  ChecklistData,
  KvListData,
  ListData,
  PitchData,
  CalloutData,
  MathData,
} from '@/types/gamePlan';

// Stable DOM id for a fragment — its anchor when present (the prototype's
// PB_GUIDES deep-link targets), else its id. This is the scroll target.
export function fragmentDomId(f: PlaybookFragment): string {
  return f.anchor ? `pb-${f.anchor}` : `pb-f-${f.id}`;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function sectionDomId(title: string): string {
  return `pb-sec-${slug(title)}`;
}

/**
 * DOM id of a Print Kit tile. Lives here rather than on the page so a fragment
 * body (e.g. a marketing idea linking to its event plan) can build the deep
 * link without importing the page that renders it.
 */
export function printAssetDomId(key: string): string {
  return `pk-${key}`;
}

/** In-chapter jump nav — anchors to each section (long chapters need this). */
export function SectionNav({
  titles,
  onJump,
}: {
  titles: string[];
  onJump: (title: string) => void;
}) {
  const jumpable = titles.filter((t) => t !== 'Overview');
  if (jumpable.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {jumpable.map((title) => (
        <button
          key={title}
          type="button"
          onClick={() => onJump(title)}
          className="rounded-full border border-[var(--border-default)] bg-[var(--background-card)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-content-title)] transition-colors hover:border-[var(--color-gold-border)] hover:bg-[var(--background-card-hover)]"
        >
          {title}
        </button>
      ))}
    </div>
  );
}

// ---------- shared primitives (brand tokens only) ----------

function RichText({ text }: { text: string }) {
  const paras = text.split(/\n\n+/);
  return (
    <div className="flex flex-col gap-3">
      {paras.map((para, i) => {
        const lines = para.split('\n');
        if (lines.every((l) => l.trim().startsWith('- '))) {
          return <Bullets key={i} items={lines.map((l) => l.replace(/^\s*-\s*/, ''))} />;
        }
        return (
          <p key={i} className="text-[14px] leading-relaxed text-[var(--text-content-title)]">
            {para}
          </p>
        );
      })}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed text-[var(--text-content-title)]">
          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-gold-500)]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function NumberedSteps({ steps }: { steps: { label: string; text?: string }[] }) {
  return (
    <ol className="flex flex-col gap-3">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(12,61,45,0.08)] text-[12px] font-semibold text-[var(--color-forest)]">
            {i + 1}
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[14px] font-medium text-[var(--text-content-title)]">{s.label}</span>
            {s.text && (
              <span className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">{s.text}</span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function KvTable({ rows, note }: { rows: { label: string; value: string }[]; note?: string | null }) {
  // Right-aligned value rows only read well for SHORT values (metrics/prices).
  // When any value is long prose, switch to stacked labeled blocks (left-aligned).
  const isLong = rows.some((r) => r.value.length > 32);

  if (isLong) {
    return (
      <div className="flex flex-col gap-3">
        {rows.map((r, i) => (
          <LabeledBlock key={i} label={r.label}>
            <span>{r.value}</span>
          </LabeledBlock>
        ))}
        {note && <p className="text-[13px] leading-relaxed text-[var(--text-content-sub-title)]">{note}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-hidden rounded-[8px] border border-[var(--border-default)]">
        {rows.map((r, i) => (
          <div
            key={i}
            className={
              'flex items-start justify-between gap-4 px-4 py-2.5 text-[14px]' +
              (i > 0 ? ' border-t border-[var(--border-default)]' : '')
            }
          >
            <span className="font-medium text-[var(--text-content-sub-title)]">{r.label}</span>
            <span className="text-right font-semibold text-[var(--text-content-title)]">{r.value}</span>
          </div>
        ))}
      </div>
      {note && <p className="text-[13px] leading-relaxed text-[var(--text-content-sub-title)]">{note}</p>}
    </div>
  );
}

function LabeledBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--color-text-gold)]">
        {label}
      </span>
      <div className="text-[14px] leading-relaxed text-[var(--text-content-title)]">{children}</div>
    </div>
  );
}

function GoldBadge({ text }: { text: string }) {
  return (
    <span className="shrink-0 rounded-full bg-[rgba(181,143,45,0.12)] px-2.5 py-1 text-[12px] font-semibold text-[var(--color-text-gold)]">
      {text}
    </span>
  );
}

/** Big-number metric tile. The main "pop" element. */
function StatTile({ metric, label, note }: { metric: string; label: string; note?: string | null }) {
  return (
    <div className="flex flex-col gap-1 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5">
      <span className="text-[32px] font-extrabold leading-none text-[var(--color-metric-highlight)]">
        {metric}
      </span>
      <span className="text-[13px] font-semibold text-[var(--text-content-title)]">{label}</span>
      {note && <span className="text-[12px] leading-relaxed text-[var(--text-content-sub-title)]">{note}</span>}
    </div>
  );
}

// ---------- layout bodies ----------

function PhaseBody({ d }: { d: PhaseData }) {
  return (
    <LabeledBlock label="Focus">
      <Bullets items={d.focus} />
    </LabeledBlock>
  );
}

function FunnelStepBody({ d }: { d: FunnelStepData }) {
  return (
    <div className="flex flex-col gap-3">
      <RichText text={d.activity} />
      {d.benchmark && (
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--color-text-gold)]">
            Benchmark
          </span>
          <span className="text-[16px] font-bold text-[var(--color-forest)]">{d.benchmark}</span>
        </div>
      )}
      {d.boldNote && (
        <p className="rounded-[8px] border-l-2 border-[var(--color-forest)] bg-[rgba(12,61,45,0.05)] px-3 py-2 text-[14px] font-medium text-[var(--color-forest)]">
          {d.boldNote}
        </p>
      )}
    </div>
  );
}

/** Small copy-to-clipboard control for the "Lead with" pitch line. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="rounded-[6px] border border-[rgba(251,246,236,0.3)] bg-[rgba(251,246,236,0.1)] px-2 py-1 text-[11px] font-semibold text-[var(--text-on-forest)] transition-colors hover:bg-[rgba(251,246,236,0.18)]"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function PersonaBody({ d }: { d: PersonaData }) {
  const cares = d.caresAbout
    .split(/,\s*/)
    .map((c) => c.replace(/\.$/, '').trim())
    .filter(Boolean);
  const [open, setOpen] = useState(false);
  const objections = d.objections ?? [];

  return (
    <div className="flex flex-col gap-3">
      <LabeledBlock label="Who they are">
        <span>{d.who}</span>
      </LabeledBlock>

      {/* Cares about → chips */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--color-text-gold)]">
          Cares about
        </span>
        <div className="flex flex-wrap gap-1.5">
          {cares.map((c, i) => (
            <span
              key={i}
              className="rounded-full bg-[var(--background-badge-green)] px-2.5 py-1 text-[12px] font-medium text-[var(--text-badge-green)]"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Lead with → forest pitch card + copy */}
      <div className="rounded-[10px] bg-[var(--color-forest)] p-4 text-[var(--text-on-forest)]">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-on-forest-muted)]">
            Lead with
          </span>
          <CopyButton text={d.leadWith} />
        </div>
        <p className="text-[14px] leading-relaxed">{d.leadWith}</p>
      </div>

      {/* Objections → expandable, they-say / you-say */}
      {objections.length > 0 && (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center justify-between gap-2 text-[13px] font-semibold text-[var(--color-forest)]"
          >
            <span>{objections.length} objections · how to handle them</span>
            <span className="text-[var(--color-text-gold)]">{open ? 'Hide −' : 'Show +'}</span>
          </button>
          {open && (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {objections.map((o, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-2 rounded-[10px] border border-[var(--border-default)] p-3"
                >
                  <div className="rounded-[8px] rounded-bl-sm bg-[var(--background-page-header)] px-3 py-2 text-[13px] italic text-[var(--text-content-title)]">
                    {o.obj}
                  </div>
                  <div className="rounded-[8px] rounded-br-sm bg-[rgba(12,61,45,0.06)] px-3 py-2 text-[13px] leading-relaxed text-[var(--text-content-title)]">
                    {o.resp}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdeaBody({ d }: { d: IdeaData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Some ideas ship with a full printable template. Rather than duplicating the
  // sheet here, the idea points at its tile in the Print Kit chapter.
  const printHref = d.printAssetKey
    ? `${location.pathname}?chapter=print-kit#${printAssetDomId(d.printAssetKey)}`
    : undefined;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">{d.goal}</p>
      <Bullets items={d.actions} />
      {printHref && (
        <TeeLink
          variant="primary"
          size="m"
          href={printHref}
          rightIcon={<span aria-hidden>→</span>}
          onClick={(e) => {
            // Keep the real href so middle-click / copy-link still work, but
            // route in-app on a plain left click.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate({
              pathname: location.pathname,
              search: '?chapter=print-kit',
              hash: `#${printAssetDomId(d.printAssetKey as string)}`,
            });
          }}
        >
          {d.linkLabel ?? t('gamePlan.ideaFullEventPlan', 'Full event plan')}
        </TeeLink>
      )}
    </div>
  );
}

function PostIdeaBody({ d }: { d: PostIdeaData }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] leading-relaxed text-[var(--text-content-title)]">{d.text}</p>
      {d.script && <LabeledBlock label="Script"><RichText text={d.script} /></LabeledBlock>}
      {d.howTo && <LabeledBlock label="How to post it"><span>{d.howTo}</span></LabeledBlock>}
      {d.whoToUse && <LabeledBlock label="Who to use"><span>{d.whoToUse}</span></LabeledBlock>}
      {d.style && <LabeledBlock label="Style"><span>{d.style}</span></LabeledBlock>}
      {d.cta && <LabeledBlock label="CTA"><span>{d.cta}</span></LabeledBlock>}
    </div>
  );
}

function ScheduleBody({ d }: { d: ScheduleData }) {
  return (
    <div className="flex flex-col gap-3">
      <LabeledBlock label="Phase"><span>{d.phase}</span></LabeledBlock>
      <div className="flex flex-col gap-2">
        {d.items.map((it, i) => (
          <div key={i} className="flex flex-col gap-1 rounded-[8px] bg-[var(--background-page-header)] px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-[var(--text-content-title)]">{it.day}</span>
              <TeeBadge label={it.format} type="grey" size="s" />
            </div>
            <span className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">{it.idea}</span>
          </div>
        ))}
      </div>
      {d.note && (
        <p className="rounded-[8px] border-l-2 border-[var(--color-gold-500)] bg-[rgba(181,143,45,0.08)] px-3 py-2 text-[13px] font-medium text-[var(--color-text-gold)]">
          {d.note}
        </p>
      )}
    </div>
  );
}

function HowToBody({ d }: { d: HowToData }) {
  return <NumberedSteps steps={d.steps.map((s) => ({ label: s }))} />;
}

function OutreachStepBody({ d }: { d: OutreachStepData }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] font-medium leading-relaxed text-[var(--text-content-title)]">{d.summary}</p>
      <p className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">{d.intro}</p>
      <p className="rounded-[8px] border-l-2 border-[var(--color-gold-500)] bg-[rgba(181,143,45,0.08)] px-3 py-2 text-[14px] font-medium text-[var(--color-text-gold)]">
        Try this first: {d.tryThis}
      </p>
      <NumberedSteps steps={d.steps.map((s) => ({ label: s.name, text: s.text }))} />
    </div>
  );
}

function PitchBody({ d }: { d: PitchData }) {
  return (
    <div className="flex flex-col gap-2.5">
      {d.pairs.map((p, i) => (
        <div key={i} className="flex gap-2.5 text-[14px] leading-relaxed text-[var(--text-content-title)]">
          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-gold-500)]" />
          <span>
            <b className="font-semibold text-[var(--color-forest)]">{p.lead}</b> {p.rest}
          </span>
        </div>
      ))}
    </div>
  );
}

function CalloutBody({ d }: { d: CalloutData }) {
  const forest = d.tone === 'forest';
  const accent = forest ? 'var(--color-forest)' : 'var(--color-maroon)';
  const bg = forest ? 'rgba(12,61,45,0.06)' : 'rgba(146,52,48,0.06)';
  return (
    <div className="rounded-[10px] border-l-[3px] p-4" style={{ borderColor: accent, background: bg }}>
      <p className="mb-1 text-[13px] font-bold uppercase tracking-wide" style={{ color: accent }}>
        {d.title}
      </p>
      <p className="text-[14px] leading-relaxed text-[var(--text-content-title)]">{d.text}</p>
    </div>
  );
}

/** The goal → leads calculation (ported from the prototype's .math box). */
function MathBody({ d }: { d: MathData }) {
  const box = (label: string, value: React.ReactNode) => (
    <div className="flex min-w-[96px] flex-col items-center gap-1 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] px-4 py-3">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]">
        {label}
      </span>
      <span className="text-[24px] font-extrabold leading-none text-[var(--color-metric-highlight)]">
        {value}
      </span>
    </div>
  );
  const op = (s: string) => (
    <span className="text-[20px] font-bold text-[var(--color-gold-500)]">{s}</span>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {box('Member Goal', d.goal)}
        {op('÷')}
        {box('Assumed Conversion', d.conversion)}
        {op('=')}
        {box('Leads Needed', d.leads)}
      </div>
      <p className="text-[13px] text-[var(--text-content-sub-title)]">
        That's <b className="font-semibold text-[var(--color-forest)]">{d.pace}</b> across the 16-week runway.
      </p>
    </div>
  );
}

function ChecklistBody({ d }: { d: ChecklistData }) {
  return (
    <ul className="flex flex-col gap-2">
      {d.items.map((item, i) => (
        <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed text-[var(--text-content-title)]">
          {/* Empty box — this is a checklist to tick off, not a done list. */}
          <span className="mt-0.5 h-4 w-4 shrink-0 rounded-[4px] border border-[var(--color-forest)]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function headerBadge(fragment: PlaybookFragment): React.ReactNode {
  switch (fragment.layout) {
    case 'phase':
      return <GoldBadge text={`${(fragment.data as unknown as PhaseData).target} members`} />;
    case 'idea': {
      const d = fragment.data as unknown as IdeaData;
      if (d.cost) return <GoldBadge text={d.cost} />;
      if (d.tierLabel) return <TeeBadge label={d.tierLabel} type="grey" size="s" />;
      return null;
    }
    case 'post-idea':
      return <TeeBadge label={(fragment.data as unknown as PostIdeaData).format} type="grey" size="s" />;
    default:
      return null;
  }
}

/** The structured body for a fragment's layout (no outer card). */
function LayoutBody({ fragment }: { fragment: PlaybookFragment }) {
  const d = fragment.data;
  switch (fragment.layout) {
    case 'phase':
      return <PhaseBody d={d as unknown as PhaseData} />;
    case 'funnel-step':
      return <FunnelStepBody d={d as unknown as FunnelStepData} />;
    case 'persona':
      return <PersonaBody d={d as unknown as PersonaData} />;
    case 'idea':
      return <IdeaBody d={d as unknown as IdeaData} />;
    case 'post-idea':
      return <PostIdeaBody d={d as unknown as PostIdeaData} />;
    case 'schedule':
      return <ScheduleBody d={d as unknown as ScheduleData} />;
    case 'how-to':
      return <HowToBody d={d as unknown as HowToData} />;
    case 'outreach-step':
      return <OutreachStepBody d={d as unknown as OutreachStepData} />;
    case 'checklist':
      return <ChecklistBody d={d as unknown as ChecklistData} />;
    case 'kv-list': {
      const kv = d as unknown as KvListData;
      return <KvTable rows={kv.rows} note={kv.note} />;
    }
    case 'list':
      return <Bullets items={(d as unknown as ListData).items} />;
    case 'pitch':
      return <PitchBody d={d as unknown as PitchData} />;
    case 'callout':
      return <CalloutBody d={d as unknown as CalloutData} />;
    case 'math':
      return <MathBody d={d as unknown as MathData} />;
    case 'prose':
    default:
      return <RichText text={fragment.body} />;
  }
}

/** A single boxed fragment (used by card grids / stacks). */
function FragmentCard({ fragment }: { fragment: PlaybookFragment }) {
  const badge = headerBadge(fragment);
  return (
    <article
      id={fragmentDomId(fragment)}
      className="scroll-mt-4 flex flex-col rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-[var(--text-content-title)]">
          {fragment.label || fragment.title}
        </h3>
        {badge}
      </div>
      <LayoutBody fragment={fragment} />
    </article>
  );
}

/** Leading integer in a "Week N" label, for ordering the tabs; 0 if none. */
function weekNumber(label?: string): number {
  const match = (label ?? '').match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

/**
 * Weekly Posting Schedule as a scrollable Week 1..16 tab strip: one tab per
 * week fragment (ordered by week number), with the selected week's card shown
 * below. Replaces the long vertical stack so a coach can jump straight to a
 * week. Self-contained local state — the schedule is a single section with no
 * cross-section coordination.
 */
function WeeklyScheduleTabs({ fragments }: { fragments: PlaybookFragment[] }) {
  const weeks = [...fragments].sort((a, b) => weekNumber(a.label) - weekNumber(b.label));
  const [activeIndex, setActiveIndex] = useState(0);
  const current = weeks[activeIndex] ?? weeks[0];

  return (
    <div className="flex flex-col gap-4">
      {/* Wraps rather than scrolls. The section is capped at max-w-[80ch]
          (~620px) but 16 "Week N" tabs need ~1290px, so a horizontal scroller
          put weeks 11–16 off-screen — and macOS hides the scrollbar until you
          scroll, so the schedule looked like it stopped at Week 10. Two rows of
          chips show the whole 16-week run at once. */}
      <div
        role="tablist"
        aria-label="Weekly posting schedule"
        className="flex flex-wrap gap-2"
      >
        {weeks.map((f, i) => {
          const selected = i === activeIndex;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActiveIndex(i)}
              className={
                'shrink-0 rounded-[8px] border px-3 py-1.5 text-[13px] transition-colors ' +
                (selected
                  ? 'bg-[var(--color-forest)] border-[var(--color-forest)] text-[var(--text-on-forest)] font-semibold'
                  : 'bg-[var(--background-card)] border-[var(--border-default)] text-[var(--text-content-title)] font-medium hover:bg-[var(--background-card-hover)]')
              }
            >
              {f.label || `Week ${i + 1}`}
            </button>
          );
        })}
      </div>
      {current && <FragmentCard fragment={current} />}
    </div>
  );
}

// ---------- section-level display ----------

type SectionMode =
  | 'lede'
  | 'phases'
  | 'grid'
  | 'stack'
  | 'stats'
  | 'price'
  | 'merged'
  | 'pillars'
  | 'blocks'
  | 'funnel'
  | 'numbered-cards'
  | 'weekly-tabs'
  | 'calendar';

function sectionMode(chapterId: string, section: PlaybookSection): SectionMode {
  if (section.title === 'Overview') return 'lede';
  // Social Media > Weekly Posting Schedule: 16 week fragments shown as a
  // scrollable Week 1..16 tab strip rather than a long vertical stack.
  if (section.title === 'Weekly Posting Schedule') return 'weekly-tabs';
  if (section.title === 'The Three Pillars') return 'pillars';
  if (section.title === 'Weekly Non-Negotiables') return 'numbered-cards';
  if (section.title === 'A Community Calendar') return 'calendar';
  const layout = section.fragments[0]?.layout;
  if (layout === 'pitch' || layout === 'callout' || layout === 'math') return 'blocks';
  if (layout === 'phase') return 'phases';
  if (layout === 'funnel-step') return 'funnel';
  if (layout === 'idea' || layout === 'post-idea') return 'grid';
  // Personas carry chips + a pitch card + a 2-col objections grid inside each
  // card, so they need full width (matches the prototype).
  if (
    layout === 'persona' ||
    layout === 'schedule' ||
    layout === 'how-to' ||
    layout === 'outreach-step'
  )
    return 'stack';
  if (layout === 'kv-list') {
    if (chapterId === 'kpi') return 'stats';
    if (section.title === 'Pricing') return 'price';
    return 'merged';
  }
  return 'merged'; // prose one-liners, lists, checklists
}

function SectionHeading({ title, note }: { title: string; note?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <span className="h-[3px] w-6 shrink-0 rounded-full bg-[var(--color-gold-500)]" />
        <h2 className="text-[13px] font-bold uppercase tracking-widest text-[var(--text-content-title)]">{title}</h2>
      </div>
      {note && (
        <p className="max-w-[72ch] text-[13px] leading-relaxed text-[var(--text-content-sub-title)]">{note}</p>
      )}
    </div>
  );
}

// The 5 phases in order (foundation → ramp → convert → push → countdown),
// mapped to brand glyphs (ported from the prototype's phase icons).
const PHASE_GLYPHS = [IconChartBar, IconTrendingUp, IconTarget, IconRocket, IconClock];

/** A timeline phase card — glyph + title + target badge + focus bullets. */
function PhaseCard({ fragment, index }: { fragment: PlaybookFragment; index: number }) {
  const d = fragment.data as unknown as PhaseData;
  const Glyph = PHASE_GLYPHS[index] ?? IconTargetArrow;
  return (
    <article
      id={fragmentDomId(fragment)}
      className="scroll-mt-4 flex flex-col rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(12,61,45,0.08)] text-[var(--color-forest)]">
            <Glyph size={18} />
          </span>
          <h3 className="text-[15px] font-semibold text-[var(--text-content-title)]">{fragment.title}</h3>
        </div>
        <GoldBadge text={`${d.target} members`} />
      </div>
      <LabeledBlock label="Focus">
        <Bullets items={d.focus} />
      </LabeledBlock>
    </article>
  );
}

/** The member-target progression across the timeline phases. */
function PhaseProgression({ fragments }: { fragments: PlaybookFragment[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] px-5 py-4">
      {fragments.map((f, i) => {
        const d = f.data as unknown as PhaseData;
        const Glyph = PHASE_GLYPHS[i] ?? IconTargetArrow;
        return (
          <div key={f.id} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-0.5">
              <Glyph size={16} className="text-[var(--color-text-gold)]" />
              <span className="text-[22px] font-extrabold leading-none text-[var(--color-metric-highlight)]">
                {d.target}
              </span>
              <span className="mt-0.5 text-[11px] text-[var(--text-content-sub-title)]">{d.range}</span>
            </div>
            {i < fragments.length - 1 && (
              <span className="text-[16px] text-[var(--color-gold-500)]">→</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PILLAR_ICONS: Record<string, React.ComponentType<any>> = {
  Body: IconBarbell,
  Swing: IconTargetArrow,
  Equipment: IconTool,
};

/** The Three Pillars — 3-col icon cards (ported from the prototype's .pill-card). */
function PillarsGrid({ fragments }: { fragments: PlaybookFragment[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {fragments.map((f) => {
        const Icon = PILLAR_ICONS[f.label] ?? IconTargetArrow;
        return (
          <div
            key={f.id}
            id={fragmentDomId(f)}
            className="scroll-mt-4 flex flex-col gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(12,61,45,0.08)] text-[var(--color-forest)]">
              <Icon size={18} />
            </span>
            <h3 className="text-[16px] font-semibold text-[var(--text-content-title)]">{f.label}</h3>
            <p className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">{f.body}</p>
          </div>
        );
      })}
    </div>
  );
}

/** The Lead Funnel — horizontal rows (ported from the prototype's .fun-bar). */
function Funnel({ fragments }: { fragments: PlaybookFragment[] }) {
  return (
    <div className="flex flex-col gap-3">
      {fragments.map((f) => {
        const d = f.data as unknown as FunnelStepData;
        return (
          <div
            key={f.id}
            id={fragmentDomId(f)}
            className="scroll-mt-4 flex items-start gap-4 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-4"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-forest)] text-[14px] font-bold text-[var(--text-on-forest)]">
              {d.step}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <h3 className="text-[15px] font-semibold text-[var(--text-content-title)]">{d.name}</h3>
              <p className="text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">
                {d.activity}
                {d.boldNote && <b className="font-semibold text-[var(--color-forest)]"> {d.boldNote}</b>}
              </p>
            </div>
            {d.benchmark && (
              <div className="flex shrink-0 flex-col items-end text-right">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--color-text-gold)]">
                  Target
                </span>
                <span className="text-[15px] font-bold text-[var(--color-forest)]">{d.benchmark}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** One live Founding Membership price card (resolved from a real location plan). */
export interface FoundingPriceCard {
  label: string;
  amount: string;
  period: string;
  sub?: string;
}

/**
 * Live pricing override for the Founding Member Offer "Pricing" section. When
 * present it replaces the static seed placeholder with the location's real
 * Performance / Performance Annual plan pricing. `header` carries the location
 * label + switcher so owners see (and can change) which location they're viewing.
 */
export interface FoundingPricingOverride {
  cards: FoundingPriceCard[];
  note?: string;
  header?: React.ReactNode;
}

function PriceCards({
  fragment,
  override,
}: {
  fragment: PlaybookFragment;
  override?: FoundingPricingOverride;
}) {
  if (override) {
    return (
      <div className="flex flex-col gap-3">
        {override.header}
        <div className="grid grid-cols-2 gap-4">
          {override.cards.map((c, i) => (
            <div key={i} className="flex flex-col gap-1 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5">
              <span className="text-[13px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]">
                {c.label}
              </span>
              <span className="text-[28px] font-extrabold leading-none text-[var(--color-forest)]">{c.amount}</span>
              {c.period && <span className="text-[13px] text-[var(--text-content-sub-title)]">/ {c.period}</span>}
              {c.sub && <span className="mt-1 text-[12px] text-[var(--text-content-sub-title)]">{c.sub}</span>}
            </div>
          ))}
        </div>
        {override.note && <p className="text-[13px] leading-relaxed text-[var(--text-content-sub-title)]">{override.note}</p>}
      </div>
    );
  }

  const kv = fragment.data as unknown as KvListData;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-4">
        {kv.rows.map((r, i) => {
          const [amount, period] = r.value.split('/');
          return (
            <div key={i} className="flex flex-col gap-1 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5">
              <span className="text-[13px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]">
                {r.label}
              </span>
              <span className="text-[28px] font-extrabold leading-none text-[var(--color-forest)]">{amount}</span>
              {period && <span className="text-[13px] text-[var(--text-content-sub-title)]">/ {period}</span>}
            </div>
          );
        })}
      </div>
      {kv.note && <p className="text-[13px] leading-relaxed text-[var(--text-content-sub-title)]">{kv.note}</p>}
    </div>
  );
}

/** Renders one chapter section with the treatment its content calls for. */
export function SectionBlock({
  chapterId,
  section,
  foundingPricing,
}: {
  chapterId: string;
  section: PlaybookSection;
  foundingPricing?: FoundingPricingOverride;
}) {
  const mode = sectionMode(chapterId, section);

  // Lede: flowing prose, no header, no box.
  if (mode === 'lede') {
    // Flowing prose, but render each fragment by its layout so an Overview can
    // carry e.g. the Game Plan "Critical Dependency" callout.
    return (
      <div className="flex max-w-[72ch] flex-col gap-4">
        {section.fragments.map((f) => (
          <div key={f.id} id={fragmentDomId(f)} className="scroll-mt-4">
            <LayoutBody fragment={f} />
          </div>
        ))}
      </div>
    );
  }

  const heading = <SectionHeading title={section.title} note={section.note} />;
  let content: React.ReactNode;

  if (mode === 'pillars') {
    content = (
      <>
        {heading}
        <PillarsGrid fragments={section.fragments} />
      </>
    );
  } else if (mode === 'funnel') {
    content = (
      <>
        {heading}
        <Funnel fragments={section.fragments} />
      </>
    );
  } else if (mode === 'calendar') {
    // One consistent Rhythm · What · Who table (each fragment = one rhythm row),
    // instead of per-item kv boxes that flip between aligned/stacked.
    const rowOf = (f: PlaybookFragment, label: string) =>
      (f.data as unknown as KvListData).rows.find((r) => r.label === label)?.value ?? '';
    content = (
      <>
        {heading}
        <div className="overflow-hidden rounded-[10px] border border-[var(--border-default)]">
          <div className="grid grid-cols-[120px_1fr_1fr] bg-[var(--background-page-header)] px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]">
            <span>Rhythm</span>
            <span>What</span>
            <span>Who</span>
          </div>
          {section.fragments.map((f, i) => (
            <div
              key={f.id}
              id={fragmentDomId(f)}
              className={
                'grid grid-cols-[120px_1fr_1fr] items-start gap-2 px-4 py-3 text-[14px] scroll-mt-4' +
                (i > 0 ? ' border-t border-[var(--border-default)]' : '')
              }
            >
              <span className="font-semibold text-[var(--color-forest)]">{f.label}</span>
              <span className="text-[var(--text-content-title)]">{rowOf(f, 'What')}</span>
              <span className="text-[var(--text-content-sub-title)]">{rowOf(f, 'Who')}</span>
            </div>
          ))}
        </div>
      </>
    );
  } else if (mode === 'numbered-cards') {
    // 2-col grid of number-marker cards (ported from the prototype's .g2/.pb-card.row-c).
    content = (
      <>
        {heading}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {section.fragments.map((f, i) => (
            <div
              key={f.id}
              id={fragmentDomId(f)}
              className="scroll-mt-4 flex items-start gap-3 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-4"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-gold-subtle)] text-[13px] font-bold text-[var(--color-text-gold)]">
                {i + 1}
              </span>
              <p className="text-[14px] leading-relaxed text-[var(--text-content-title)]">{f.body}</p>
            </div>
          ))}
        </div>
      </>
    );
  } else if (mode === 'blocks') {
    // Distinct blocks, each rendered by its own layout (pitch bullets, callout
    // box, …) — no shared card, no numbering.
    content = (
      <>
        {heading}
        <div className="flex flex-col gap-5">
          {section.fragments.map((f) => (
            <div key={f.id} id={fragmentDomId(f)} className="scroll-mt-4 flex flex-col gap-2">
              {f.label && f.layout !== 'callout' && (
                <h3 className="text-[15px] font-semibold text-[var(--text-content-title)]">{f.label}</h3>
              )}
              <LayoutBody fragment={f} />
            </div>
          ))}
        </div>
      </>
    );
  } else if (mode === 'phases') {
    content = (
      <>
        {heading}
        <PhaseProgression fragments={section.fragments} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {section.fragments.map((f, i) => (
            <PhaseCard key={f.id} fragment={f} index={i} />
          ))}
        </div>
      </>
    );
  } else if (mode === 'grid') {
    content = (
      <>
        {heading}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {section.fragments.map((f) => (
            <FragmentCard key={f.id} fragment={f} />
          ))}
        </div>
      </>
    );
  } else if (mode === 'stack') {
    content = (
      <>
        {heading}
        {section.fragments.map((f) => (
          <FragmentCard key={f.id} fragment={f} />
        ))}
      </>
    );
  } else if (mode === 'weekly-tabs') {
    content = (
      <>
        {heading}
        <WeeklyScheduleTabs fragments={section.fragments} />
      </>
    );
  } else if (mode === 'stats') {
    content = (
      <>
        {heading}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {section.fragments.map((f) => {
            const d = f.data as { label?: string; metric?: string; note?: string | null };
            return (
              <div key={f.id} id={fragmentDomId(f)} className="scroll-mt-4">
                <StatTile metric={d.metric ?? ''} label={d.label ?? f.label ?? f.title} note={d.note} />
              </div>
            );
          })}
        </div>
      </>
    );
  } else if (mode === 'price') {
    content = (
      <>
        {heading}
        {section.fragments.map((f, i) => (
          <div key={f.id} id={fragmentDomId(f)} className="scroll-mt-4">
            {/* Live pricing replaces only the first (placeholder) pricing fragment. */}
            <PriceCards fragment={f} override={i === 0 ? foundingPricing : undefined} />
          </div>
        ))}
      </>
    );
  } else {
    // merged: one framed card; items as a consistent numbered list. The label
    // (de-duped — no repeated section prefix) is the item heading; '' = none.
    content = (
      <>
        {heading}
        <div className="flex flex-col gap-5 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-5">
          {section.fragments.map((f, i) => (
            <div
              key={f.id}
              id={fragmentDomId(f)}
              className={'scroll-mt-4 flex gap-3' + (i > 0 ? ' border-t border-[var(--border-default)] pt-5' : '')}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(12,61,45,0.08)] text-[12px] font-semibold text-[var(--color-forest)]">
                {i + 1}
              </span>
              <div className="flex flex-1 flex-col gap-2">
                {f.label && (
                  <h3 className="text-[15px] font-semibold text-[var(--text-content-title)]">{f.label}</h3>
                )}
                <LayoutBody fragment={f} />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  const narrow =
    mode === 'stack' ||
    mode === 'price' ||
    mode === 'merged' ||
    mode === 'blocks' ||
    mode === 'weekly-tabs';
  return (
    <section
      id={sectionDomId(section.title)}
      className={'flex scroll-mt-4 flex-col gap-4' + (narrow ? ' max-w-[80ch]' : '')}
    >
      {content}
    </section>
  );
}
